/**
 * SOLID Review
 * S — Strong. One job: the Apollo client — the HTTP and websocket links, the auth header, the split between them, the
 *     slow-request hint — built once.
 * O — Good. A new link is one more step in `create()`.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. `create()`, and the HTTP URL rule the composition root reads the environment for.
 * D — Strong. The token store, the websocket client, the banner's handlers, the timers and `fetch` are handed in.
 */
import { ApolloClient, InMemoryCache, HttpLink, split } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { setContext } from '@apollo/client/link/context';
import type { Client } from 'graphql-ws';
import { PROD_GRAPHQL_HTTP_URL } from '../../shared/appConfig';
import type { SessionTokenStore } from '../storage/SessionTokenStore';
import type { IServiceTimers } from '../platform/IServiceTimers';
import { SlowRequestLink, type IServiceSlowRequestLinkOptions } from './SlowRequestLink';

/**
 * The server-busy hint's two moments. This is Infrastructure, so it must not reach into the UI's state: the composition root
 * hands the handlers that do (`framework/composition/frontendRoot.ts`).
 */
export interface IConfigHandlerConnectionBanner {
  readonly onSlowStart: () => void;
  readonly onSlowEnd: () => void;
}

/** What the Apollo client is built from — the composition root hands the browser's, a test its own. */
export interface IConfigDepApolloClient {
  /** Where queries and mutations go (`ApolloClientFactory.httpUrlFor`). */
  readonly httpUrl: string;
  /** How they get there: the browser's `fetch`, looked up when a request goes out. */
  readonly fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  /** The tab's token: the `Authorization` header of every HTTP request. */
  readonly tokens: SessionTokenStore;
  /** Where subscriptions go: the websocket monitor's graphql-ws client. */
  readonly ws: Client;
  readonly banner: IConfigHandlerConnectionBanner;
  readonly timers: IServiceTimers;
}

/** How long a mutation may run before the server-busy hint shows. */
const SLOW_MUTATION_MS = 1500;

/**
 * THE APOLLO CLIENT, BUILT ONCE (frontend-infrastructure-oo P2, 2026-10-28, §28.287) — `apolloClient.ts`, which built itself on
 * import, as an object the composition root builds from what it hands in: the module's banner handlers (a `let` behind a setter
 * the root called) are handed in whole, as the websocket client, the token and the URL are. Behaviour unchanged.
 */
export class ApolloClientFactory {
  constructor(private readonly deps: IConfigDepApolloClient) {}

  /**
   * Where queries and mutations go: an explicit `VITE_GRAPHQL_HTTP_URL`, else in development the page's own host, else
   * production's. Read by the composition root, which reads the environment.
   */
  static httpUrlFor(env: Record<string, unknown>, location: { protocol: string; host: string }): string {
    const isDev = env['DEV'] === 'true' || env['DEV'] === true;
    return (env['VITE_GRAPHQL_HTTP_URL'] as string | undefined) ?? (
      isDev
        ? `${location.protocol}//${location.host}/graphql`
        : PROD_GRAPHQL_HTTP_URL
    );
  }

  create(): ApolloClient {
    const { deps } = this;
    const httpLink = new HttpLink({ uri: deps.httpUrl, fetch: deps.fetch });
    // The auth link injects the Authorization header on every HTTP request.
    const authLink = setContext((_, previousContext: Record<string, unknown>) => {
      const token = deps.tokens.token();
      const existingHeaders = (previousContext['headers'] as Record<string, string> | undefined) ?? {};
      return {
        headers: {
          ...existingHeaders,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      };
    });
    const wsLink = new GraphQLWsLink(deps.ws);
    // Subscriptions go over the websocket, everything else over HTTP.
    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      authLink.concat(httpLink),
    );
    // A transient hint when the server is struggling — mutations only (§12.36): the backend degrades gracefully under
    // saturation (slow, not erroring), and without it a player sees an unresponsive board and starts refreshing.
    const slowOptions: IServiceSlowRequestLinkOptions = {
      thresholdMs: SLOW_MUTATION_MS,
      onSlowStart: deps.banner.onSlowStart,
      onSlowEnd: deps.banner.onSlowEnd,
    };
    const slow = new SlowRequestLink(slowOptions, deps.timers);
    return new ApolloClient({
      link: slow.link.concat(splitLink),
      // NO typePolicies, deliberately (2026-08-26): six were declared for type names the backend SDL does not have, so all were
      // inert; InMemoryCache already normalises on `id`. Restore an explicit policy only for a type that needs other key fields.
      cache: new InMemoryCache(),
      // NO watchQuery defaults (frontend-infrastructure-oo final verification, §28.291): nothing watches a query — the adapters run
      // `query`, `mutate` and `subscribe` through `ApolloDomainClient`, and the React hooks that watched went with `<ApolloProvider>`.
      defaultOptions: {
        query: {
          fetchPolicy: 'network-only',
          errorPolicy: 'all' as any,
        },
        mutate: {
          errorPolicy: 'all' as any,
        },
      },
    });
  }
}
